import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet-async";
import BlogLayout from "../components/BlogLayout";
import SEOHead from "../components/SEOHead";
import { getPostBySlug } from "../content/blog";
import { getSiteUrl, SITE_NAME } from "../config/site";
import "./blog.css";

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <BlogLayout>
        <SEOHead
          title="Article Not Found"
          description="The blog article you are looking for could not be found."
          path={`/blog/${slug}`}
        />
        <div className="blog-not-found">
          <h1>Article not found</h1>
          <p>The page you are looking for does not exist.</p>
          <Link to="/blog" className="blog-back-link">
            ← Back to blog
          </Link>
        </div>
      </BlogLayout>
    );
  }

  const path = `/blog/${post.slug}`;
  const siteUrl = getSiteUrl();
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    author: {
      "@type": "Organization",
      name: SITE_NAME,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
    },
    mainEntityOfPage: `${siteUrl}${path}`,
  };

  const markdownComponents = {
    a: ({ href, children }) => {
      if (href?.startsWith("/")) {
        return <Link to={href}>{children}</Link>;
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  };

  return (
    <BlogLayout>
      <SEOHead
        title={post.title}
        description={post.description}
        path={path}
        type="article"
        publishedAt={post.publishedAt}
        keywords={post.keywords}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <Link to="/blog" className="blog-back-link">
        ← Back to blog
      </Link>

      <header className="blog-post-header">
        <h1>{post.title}</h1>
        <p className="blog-post-meta">Published {formatDate(post.publishedAt)}</p>
      </header>

      <article className="blog-article">
        <ReactMarkdown components={markdownComponents}>
          {post.content.trim()}
        </ReactMarkdown>
      </article>

      <div className="blog-cta-banner">
        <h2>Find entry-level jobs in California</h2>
        <p>
          Browse roles with no experience required — apply with purpose on
          ChapterOne Jobs.
        </p>
        <button
          type="button"
          className="blog-cta-button"
          onClick={() => navigate("/")}
        >
          Browse Jobs →
        </button>
      </div>
    </BlogLayout>
  );
}

export default BlogPost;
